import {
  ReactElement,
  useEffect,
  useCallback,
  useState,
  Suspense
} from 'react';
import { v1 as uuid } from 'uuid';
import { useTranslation } from 'react-i18next';

import spinnerOverlayModel from '../../models/spinnerOverlay';
import { emailValidation } from '../../components/EmailInput';
import Spinner from '../../components/Spinner';
import dataModel from '../../models/data';
import localeModel from '../../models/locale';
import { RequiredPolicyHiddenInputs } from '../../components/RequiredPolicies/RequiredPolicyHiddenInputs';
import analytics from '../../services/analytics';
import InvalidEmailModal from '../../modals/InvalidEmailModal';
import ChangeFastOnboardEmailModal from '../../modals/ChangeFastOnboardEmailModal';
import Footer from './footer';
import ThemeManager from '../../components/ThemeManager';
import Content from './Content';
import { ClientUserIdInput } from '../../components/ClientUserIdInput';
import { MyAccountCoreConfig } from '../../interfaces/ClientInfo';

import './styles.scss';

interface FastOnboardFormProps {
  oauthConsentTosLink: MyAccountCoreConfig['oauthConsentTosLink'];
  oauthConsentPpLink: MyAccountCoreConfig['oauthConsentPpLink'];
  oauthConsentNotice: MyAccountCoreConfig['oauthConsentNotice'];
}

export function FastOnboardForm({
  oauthConsentTosLink,
  oauthConsentPpLink,
  oauthConsentNotice
}: FastOnboardFormProps): ReactElement {
  const {
    email,
    formAuthenticityToken,
    brandingInfo: { clientId, clientName },
    country,
    currency,
    customUi: { isOnboard },
    welcomeVariant
  } = dataModel;
  const { language } = localeModel;

  const [modal, setModal] = useState<'inputEmail' | 'changeEmail' | 'none'>(
    emailValidation(email) ? 'none' : 'inputEmail'
  );

  const [emailProvidedByClient] = useState(dataModel.email);
  const [emailSetByGuest, setEmailSetByGuest] = useState(false);

  const { t } = useTranslation();

  const password = uuid();

  const notice =
    language === 'ja' ? oauthConsentNotice?.ja : oauthConsentNotice?.en;

  const onSubmit = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (confirm(t('SignUpForm.confirmation', { email: dataModel.email }))) {
        analytics.fastOnboardingSignUpButtonClicked();
        spinnerOverlayModel.show();
      } else {
        e.preventDefault();
      }
    },
    []
  );

  const onChangeEmailComplete = useCallback((newEmail?: string) => {
    setModal('none');

    if (newEmail) {
      dataModel.updateEmail(newEmail, true);

      setEmailSetByGuest(emailProvidedByClient !== newEmail);
    }
  }, []);

  useEffect(() => {
    analytics.welcomeSplashDecided(welcomeVariant);
    analytics.fastOnboardingLanding();
  }, [clientId]);

  return (
    <form
      className="fast-onboard-form"
      action={`/guests${window.location.search}`}
      method="post"
    >
      <input
        type="hidden"
        name="authenticity_token"
        value={formAuthenticityToken}
      />
      <input type="hidden" name="guest[partner_app]" value={clientId} />
      <input type="hidden" name="guest[email]" value={email as string} />
      <input type="hidden" name="guest[password]" value={password} />
      <input
        type="hidden"
        name="guest[password_confirmation]"
        value={password}
      />
      <input type="hidden" name="guest[country]" value={country} />
      <input type="hidden" name="guest[currency]" value={currency} />
      {isOnboard && (
        <input type="hidden" name="guest[is_onboard]" value="true" />
      )}
      <RequiredPolicyHiddenInputs name="guest[accepted_policies][]" />
      <ClientUserIdInput name="guest[extra_fields][client_user_id]" />

      <Suspense fallback={<Spinner className="loading-spinner" />}>
        <ThemeManager>
          <div className="content">
            <Content
              email={email}
              clientId={clientId}
              clientName={clientName}
              oauthConsentTosLink={oauthConsentTosLink}
              oauthConsentPpLink={oauthConsentPpLink}
              oauthConsentNotice={notice}
              onTriggerChangeEmail={() => setModal('changeEmail')}
              emailSetByGuest={emailSetByGuest}
            />
          </div>
          {modal === 'inputEmail' && (
            <InvalidEmailModal
              email={email}
              onComplete={onChangeEmailComplete}
            />
          )}
          {modal === 'changeEmail' && (
            <ChangeFastOnboardEmailModal onComplete={onChangeEmailComplete} />
          )}
          <Footer onSubmit={onSubmit} />
        </ThemeManager>
      </Suspense>
    </form>
  );
}
